import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SynchronizedTypewriter from "./SynchronizedTypewriter";
import LottieLite from "../common/LottieLite";
import bytemonkLogoAnimation from "../../assets/lottie/BYTEMONK-LOGO.json";

const VideoSection: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Use requestAnimationFrame for smooth performance
      requestAnimationFrame(() => {
        setMousePosition({
          x: e.clientX,
          y: e.clientY,
        });
      });
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div
      className="position-relative text-white d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        overflow: "hidden",
        backgroundColor: "#020617", // Slate 950 base
      }}
    >
      {/* Ambient Mouse Glow */}
      <motion.div
        className="position-absolute d-none d-md-block"
        animate={{
          x: mousePosition.x - 400,
          y: mousePosition.y - 400,
        }}
        transition={{
          type: "tween",
          ease: "backOut",
          duration: 0.5,
        }}
        style={{
          width: "800px",
          height: "800px",
          background:
            "radial-gradient(circle, rgba(14, 165, 233, 0.12) 0%, rgba(0,0,0,0) 60%)",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Background Overlay */}
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{ zIndex: 0 }}
      >
        <div
          className="position-absolute w-100 h-100"
          style={{
            background:
              "linear-gradient(180deg, rgba(2, 6, 23, 0.5) 0%, rgba(2, 6, 23, 0.98) 100%)",
            zIndex: 1,
          }}
        />
        <div
          className="position-absolute w-100 h-100 d-none d-md-block"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
            zIndex: 2,
            opacity: 0.4,
          }}
        />
      </div>

      {/* Foreground Content */}
      <div
        className="position-relative w-100 h-100 d-flex flex-column align-items-center justify-content-center"
        style={{
          zIndex: 3,
        }}
      >
        {/* Interactive Glassmorphism Hero Box - FULL WIDTH */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="w-100 h-100 position-relative d-flex align-items-center justify-content-center"
          style={{
            padding: "clamp(2rem, 6vw, 5rem)",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.35), rgba(2, 6, 23, 0.55))",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.05)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
            overflow: "hidden",
            marginTop: "clamp(7vh, 15vw, 5vh)"
          }}
        >
          {/* Aesthetic Corner Glows inside the Card */}
          <div
            className="position-absolute top-0 start-0 w-50 h-50"
            style={{
              background:
                "radial-gradient(circle at top left, rgba(14, 165, 233, 0.15), transparent 60%)",
              filter: "blur(40px)",
              pointerEvents: "none",
            }}
          />
          <div
            className="position-absolute bottom-0 end-0 w-50 h-50"
            style={{
              background:
                "radial-gradient(circle at bottom right, rgba(139, 92, 246, 0.1), transparent 60%)",
              filter: "blur(40px)",
              pointerEvents: "none",
            }}
          />

          <div className="row align-items-center position-relative z-1 w-100" style={{ maxWidth: "1400px" }}>
            <div className="col-lg-8 text-center text-lg-start">
              {/* Top Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill mb-4"
                style={{
                  background: "rgba(14, 165, 233, 0.15)",
                  border: "1px solid rgba(14, 165, 233, 0.3)",
                  color: "#38bdf8",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    background: "#38bdf8",
                    borderRadius: "50%",
                    boxShadow: "0 0 10px #38bdf8",
                  }}
                />
                TAKING GCECT TO NEW HEIGHTS
              </motion.div>

              {/* Typewriter Heading */}
              <div className="fw-bold mb-4 position-relative w-100">
                {/* Invisible Placeholder to reserve exact layout space across all screen sizes */}
                <div
                  className="w-100 placeholder-glow text-center text-lg-start"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "clamp(1.5rem, 3.5vw, 3rem)",
                    lineHeight: "1.3",
                    letterSpacing: "-0.02em",
                    visibility: "hidden",
                    pointerEvents: "none",
                  }}
                  aria-hidden="true"
                >
                  WELCOME TO BYTEMONK<br />
                  OFFICIAL TECH CLUB OF<br />
                  GOVERNMENT COLLEGE OF<br />
                  ENGINEERING & CERAMIC<br />
                  TECHNOLOGY
                </div>

                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center align-items-lg-start justify-content-start text-center text-lg-start"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "clamp(1.5rem, 3.5vw, 3rem)",
                    lineHeight: "1.3",
                    textShadow: "0 4px 20px rgba(0,0,0,0.5)",
                    letterSpacing: "-0.02em",
                  }}
                >
                  <SynchronizedTypewriter
                    lines={[
                      "WELCOME TO BYTEMONK",
                      "OFFICIAL TECH CLUB OF",
                      "GOVERNMENT COLLEGE OF",
                      "ENGINEERING & CERAMIC",
                      "TECHNOLOGY",
                    ]}
                    colors={[
                      "#22d3ee", // Cyan 400
                      "#f8fafc", // Slate 50
                      "#e0f2fe", // Light Blue 100
                      "#bae6fd", // Light Blue 200
                      "#7dd3fc", // Light Blue 300
                    ]}
                    totalTypingDuration={2500}
                    totalDeletingDuration={1200}
                    pauseDuration={1500}
                  />
                </div>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.5 }}
                className="mb-5 pt-3 mx-auto mx-lg-0"
                style={{
                  fontSize: "clamp(1rem, 1.5vw, 1.15rem)",
                  lineHeight: "1.8",
                  color: "rgba(255,255,255,0.75)",
                  maxWidth: "600px",
                }}
              >
                ByteMonk is GCECT's leading technology club, dedicated to fostering
                innovation, creativity, and collaboration. We empower students
                through hands-on learning in coding, AI/ML, and game development.
              </motion.p>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.8 }}
                className="d-flex flex-wrap gap-3 justify-content-center justify-content-lg-start"
              >
                <Link to="/events" className="text-decoration-none">
                  <button
                    className="btn rounded-pill px-4 py-3 fw-bold position-relative text-white"
                    style={{
                      background: "linear-gradient(135deg, #0ea5b9, #2563eb)",
                      border: "none",
                      boxShadow: "0 8px 25px rgba(14, 165, 233, 0.4)",
                      transition: "transform 0.2s, box-shadow 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 30px rgba(14, 165, 233, 0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 25px rgba(14, 165, 233, 0.4)";
                    }}
                  >
                    Explore Events
                  </button>
                </Link>
                <a
                  href="https://discord.gg/CewFGJSyFC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <button
                    className="btn rounded-pill px-4 py-3 fw-bold text-white"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                    }}
                  >
                    Join Community
                  </button>
                </a>
              </motion.div>
            </div>

            {/* Right side floating elements (desktop only) */}
            <div className="col-lg-4 d-none d-lg-flex justify-content-center align-items-center position-relative">
              {/* Abstract Glowing Sphere */}
              <motion.div
                animate={{
                  y: [-15, 15, -15],
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  width: "300px",
                  height: "300px",
                  background:
                    "conic-gradient(from 180deg at 50% 50%, #0ea5b9 0deg, #8b5cf6 180deg, #3b82f6 360deg)",
                  borderRadius: "50%",
                  filter: "blur(60px)",
                  opacity: 0.6,
                }}
              />
              {/* Orbital Rings */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
                className="position-absolute z-3"
              >
                <LottieLite
                  animationData={bytemonkLogoAnimation}
                  style={{ width: "200px", height: "auto", filter: "drop-shadow(0 0 20px rgba(14, 165, 233, 0.4))" }}
                  autoplay={true}
                  loop={true}
                />
              </motion.div>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="position-absolute"
                style={{
                  width: "350px",
                  height: "350px",
                  border: "1px dashed rgba(255,255,255,0.2)",
                  borderRadius: "50%",
                }}
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="position-absolute"
                style={{
                  width: "250px",
                  height: "250px",
                  border: "1px solid rgba(14, 165, 233, 0.3)",
                  borderRadius: "50%",
                }}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 1 }}
        className="position-absolute bottom-0 mb-4 d-none d-md-flex flex-column align-items-center"
        style={{ zIndex: 10 }}
      >
        <span
          style={{
            fontSize: "0.7rem",
            color: "rgba(255,255,255,0.4)",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          Scroll to Explore
        </span>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          style={{
            width: "2px",
            height: "40px",
            background:
              "linear-gradient(to bottom, #0ea5b9, rgba(14,165,233,0))",
            borderRadius: "2px",
          }}
        />
      </motion.div>
    </div>
  );
};

export default VideoSection;
